import React from "react";
import {
  Box,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  FormHelperText,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import {
  setInspectorId,
  updateFormField,
} from "../../../store/slices/inspection_info.slices";
import "./section.css";
import SignatureUpload from "./signature";
import { INSPECTOR_LIST } from "../../data/inspector_data";

const InspectionInfo = ({ errors = {} }) => {
  const dispatch = useDispatch();

  // Pull only required values from Redux
  const { selectedInspectorId, formData } = useSelector(
    (state) => state.ins_info
  );

  // Handles all form input updates
  const handleChange = (e) => {
    dispatch(updateFormField({ field: e.target.name, value: e.target.value }));
  };

  // Only store inspector ID in Redux
  const handleSelectInspector = (e) => {
    dispatch(setInspectorId(e.target.value));
  };

  return (
    <Box className="form-container">
      {/* Verification Type */}
      <FormControl
        className="form-field"
        error={!!errors.verificationType && !formData?.verificationType}
        required
      >
        <InputLabel>System Verification Type</InputLabel>
        <Select
          name="verificationType"
          value={formData.verificationType}
          label="System Verification Type"
          onChange={handleChange}
        >
          <MenuItem value="Initial">Initial Verification</MenuItem>
          <MenuItem value="Periodic">Periodic Verification</MenuItem>
        </Select>
        {errors.verificationType && !formData?.verificationType && (
          <FormHelperText>System Verification Type is required</FormHelperText>
        )}
      </FormControl>

      {/* Inspector Dropdown (only show names) */}
      <FormControl
        className="form-field"
        error={!!errors.inspector && !selectedInspectorId}
        required
      >
        <InputLabel>Select Inspector</InputLabel>
        <Select
          value={selectedInspectorId}
          label="Select Inspector"
          onChange={handleSelectInspector}
        >
          {INSPECTOR_LIST.map((ins) => (
            <MenuItem key={ins.id} value={ins.id}>
              {ins.name}
            </MenuItem>
          ))}
        </Select>
        {errors.inspector && !selectedInspectorId && (
          <FormHelperText>System Verification Type is required</FormHelperText>
        )}
      </FormControl>

      {/* Signature Component */}
      <SignatureUpload
        onSave={(dataURL) =>
          dispatch(updateFormField({ field: "signature", value: dataURL }))
        }
      />

      {/* Reference Inputs */}
      <TextField
        fullWidth
        label="Reference No (Manual)*"
        name="manualRef"
        value={formData.manualRef}
        onChange={handleChange}
        className="form-field"
        error={!!errors.manualRef && !formData.manualRef}
        helperText={
          errors.manualRef && !formData.manualRef && "Reference No is required"
        }
      />

      {/* Date */}
      <TextField
        fullWidth
        type="date"
        label="Date*"
        name="date"
        InputLabelProps={{ shrink: true }}
        value={formData.date}
        onChange={handleChange}
        error={!!errors.date && !formData.date}
        helperText={errors.date && !formData.date && "Date is required"}
        className="form-field"
      />

      {/* Description Dropdown */}
      <FormControl
        className="form-field"
        error={!!errors.description && !formData?.description}
        required
      >
        <InputLabel>Description of installation</InputLabel>
        <Select
          name="description"
          value={formData.description}
          label="Description Under Test"
          onChange={handleChange}
        >
          <MenuItem value="New Solar PV Install">New Solar PV Install</MenuItem>
          <MenuItem value="Old Solar PV Install">Old Solar PV Install</MenuItem>
        </Select>
        {errors.description && !formData?.description && (
          <FormHelperText>Description is required</FormHelperText>
        )}
      </FormControl>

      {/* Test Instrument */}
      <FormControl
        className="form-field"
        error={!!errors.testInstrument && !formData?.testInstrument}
        required
      >
        <InputLabel>Test Instrument</InputLabel>
        <Select
          name="testInstrument"
          value={formData.testInstrument}
          label="Test Instrument"
          onChange={handleChange}
        >
          <MenuItem value="Seaward PV 150 - Serial No. 15Q-0869">
            Seaward PV 150 - Serial No. 15Q-0869
          </MenuItem>
        </Select>
        {errors.testInstrument && !formData?.testInstrument && (
          <FormHelperText>test instrument is required</FormHelperText>
        )}
      </FormControl>

      {/* Next Inspection */}
      <TextField
        fullWidth
        label="Next inspection recommended after not more than:"
        name="nextInspectionAfter"
        type="number"
        inputProps={{ min: 0 }}
        value={formData.nextInspectionAfter}
        onChange={(e) => {
          const value = e.target.value;
          if (/^\d*$/.test(value)) {
            dispatch(updateFormField({ field: "nextInspectionAfter", value }));
          }
        }}
        className="form-field"
      />

      {/* Client Info */}
      <TextField
        fullWidth
        label="Client Name*"
        name="clientName"
        value={formData.clientName}
        onChange={handleChange}
        className="form-field"
        error={!!errors.clientName && !formData.clientName}
        helperText={
          errors.clientName && !formData.clientName && "Client name is required"
        }
      />

      <TextField
        fullWidth
        label="Installation Address"
        name="installationAddress"
        value={formData.installationAddress}
        onChange={handleChange}
        className="form-field"
        error={!!errors.installationAddress && !formData.installationAddress}
        helperText={
          errors.installationAddress &&
          !formData.installationAddress &&
          "Installation address is required"
        }
      />

      {/* Location */}
      <TextField
        fullWidth
        label="Location*"
        name="location"
        value={formData.location}
        onChange={handleChange}
        className="form-field"
        error={!!errors.location && !formData.location}
        helperText={
          errors.location && !formData.location && "Location is required"
        }
      />

      {/* Circuits Tested */}
      <FormControl
        className="form-field"
        error={!!errors.circuitTested && !formData?.circuitTested}
        required
      >
        <InputLabel>Circuits Tested</InputLabel>
        <Select
          name="circuitTested"
          value={formData.circuitTested}
          label="Circuit Tested"
          onChange={handleChange}
        >
          <MenuItem value="AC & DC">AC & DC</MenuItem>
        </Select>
        {errors.circuitTested && !formData?.circuitTested && (
          <FormHelperText>Circuit tested is required</FormHelperText>
        )}
      </FormControl>

      {/* Handover Comment */}
      <TextField
        fullWidth
        label="Comment for Handover Document"
        name="handoverComment"
        multiline
        rows={3}
        inputProps={{ maxLength: 250 }}
        value={formData.handoverComment}
        onChange={handleChange}
        className="form-field"
        helperText={`${formData.handoverComment.length}/250 characters`}
        sx={{ maxWidth: "500px" }}
      />
    </Box>
  );
};

export default InspectionInfo;
